import React, { useCallback, useMemo, useState } from "react";
import checkAiSelection from "../../../../utils/checkAiSelection";
import btnStyle from "../../../../utils/buttonStyle.json";
import FeedbackModal from "../../../../utils/feedbackModal";
import { useTranslation } from "react-i18next";
import { IoIosArrowRoundBack } from "react-icons/io";

type MultiChoiceProps = {
    question: {
        helpText: string;
        type: string;
        _id: string;
        imgUrl: string;
        questionText: string;
        answers: {
            tag: string[];
            _id: string;
            answerText: string;
            answerImgUrl: string;
            linkedQuestionId: string;
            inputType: string;
            label: string;
            required: boolean;
        }[];
        cta: {
            _id: string;
            ctaText: string;
            ctaType: string;
            linkedQuestionId: string;
        }[];
    };
    handleBackBtn: () => void;
    updateUserResponse: (response: {
        questionType: string;
        questionText: string;
        nextQuestionId: string;
        _id: string;
        action?: string;
        answerResponse: object;
        selfieResponse: Array<object>;
        selfieID: string;
        userSelfie: string;
        timeStamp: number;
    }) => void;
    responseData: {
        questionType: string;
        questionText: string;
        nextQuestionId: string;
        _id: string;
        action?: string;
        answerResponse: object;
        selfieResponse: Array<object>;
        selfieID: string;
        userSelfie: string;
        timeStamp: number;
    };
    skinConditions: object;
    aiConfig: object;
    aiType: object;
    backBtnConfig: object;
    currentQuestion: number;
};

const MultiChoice: React.FC<MultiChoiceProps> = ({
    question,
    updateUserResponse,
    handleBackBtn,
    responseData,
    selfieResponse,
    aiConfig,
    aiType,
    backBtnConfig,
    inputs,
    currentQuestion
}) => {
    const [selectedOptions, setSelectedOptions] = useState(responseData?.answerResponse || []);
    const [showWarning, setShowWarning] = useState(false);
    const [optionLimitWarning, setOptionLimitWarning] = useState(false)
    const { t } = useTranslation();

    const handleMultiOptionSelect = useCallback(
        (answer: object) => {
            const count = question.selectedOptionCount;
            const newOptions = selectedOptions.includes(answer)
                ? selectedOptions.filter((option) => option !== answer)
                : [...selectedOptions, answer];
            if (count === undefined || newOptions.length <= count) {
                setSelectedOptions(newOptions);
            }
            if (newOptions.length > count) setOptionLimitWarning(true)
        },
        [selectedOptions]
    );

    const constructResponse = useCallback(
        (cta: { _id: string; ctaText: string; ctaType: string; linkedQuestionId: string }) => ({
            questionType: question.type,
            questionText: question.questionText,
            nextQuestionId: cta?.linkedQuestionId || "",
            _id: question._id,
            action: cta?.ctaType,
            answerResponse: cta?.ctaType === "next" ? selectedOptions : [],
            selfieResponse: [],
            selfieID: "",
            userSelfie: "",
            timeStamp: new Date().getTime()
        }),
        [question.type, question._id, selectedOptions]
    );

    const handleSubmitBtn = useCallback(
        (cta: { _id: string; ctaText: string; ctaType: string; linkedQuestionId: string }) => {
            if (selectedOptions.length === 0) {
                setShowWarning(true)
                return;
            }
            updateUserResponse(constructResponse(cta));
        },
        [selectedOptions, updateUserResponse, constructResponse]
    );
    const RenderAiSelection = useMemo(
        () => ({ tag }: { tag: string[] }) => {
            if (!tag.includes("tangent")) return null;
            const highlightType = checkAiSelection(selfieResponse, tag, aiConfig, aiType);
            if (highlightType === "text")
                return <p className="tangent_global_style tangent_ai_select_text_custom tangent_options tngnt-absolute tngnt-left-0 tngnt-right-0 tngnt-top-[-15px] tngnt-border tngnt-w-fit tngnt-m-auto tngnt-px-2 tngnt-py-[2px] tngnt-rounded tngnt-bg-white tngnt-text-[#eb6969]">{t('selectedByAI')}</p>;
            if (highlightType === "image") return <p className="tangent_ai_select_text_custom tngnt-absolute tngnt-top-0 tngnt-left-[50%] tngnt-transform tngnt-translateX-[-50%]"><img src={aiType.asset} /></p>;
        },
        [selfieResponse]
    );

    const renderedAnswers = useMemo(
        () =>
            question.answers.map((answer) => {
                const isSelected = selectedOptions.includes(answer);
                return (
                    <div
                        key={answer._id}
                        id={answer._id}
                        className={
                            `tangent_global_style tangent_global_style_custom tngnt-flex tngnt-flex-col tngnt-text-center tngnt-gap-1 tngnt-p-2 tngnt-border tngnt-h-auto tngnt-justify-center tngnt-cursor-pointer tngnt-relative tngnt-shadow-md tngnt-rounded-[5px] tngnt-w-[45%] sm:tngnt-w-[35%] tngnt-text-[14px] tngnt-leading-[20px] tangent_options tangent_options_custom tangent_options_mob_custom tangent_${question._id}_options_custom tangent_${question._id}_options_mob_custom tngnt-text-black tngnt-border-black ` +
                            (isSelected
                                ? ` tangent_option_highlight_custom tangent_options_highlight_mob_custom tangent_options_custom tangent_options_mob_custom tangent_${question._id}_option_highlight_custom tangent_${question._id}_options_highlight_mob_custom tngnt-bg-[#c9c9c9] tngnt-shadow-[0px_0px_8px_2px_#d7b589] tngnt-border-[#c9c9c9]`
                                : null)
                        }
                        onClick={() => handleMultiOptionSelect(answer)}
                    >
                        {answer.answerImgUrl ? (
                            <img src={answer.answerImgUrl} alt="answer option image" className={`tngnt-w-[100%] tngnt-self-center tangent_option_image_custom tangent_option_image_mob_custom tangent_${question._id}_option_image_custom tangent_${question._id}_option_image_mob_custom` + (isSelected ? ` tangent_option_highlight_image_custom tangent_${question._id}_option_highlight_image_custom tangent_option_image_custom tangent_option_image_mob_custom` : null)} />
                        ) : null}
                        <div className={`tngnt-m-1 tngnt-option-text tangent_option_text_custom tangent_option_text_mob_custom tangent_${question._id}_option_text_custom tangent_${question._id}_option_text_mob_custom`} dangerouslySetInnerHTML={{ __html: eval('`' + answer.answerText + '`') }} />
                        <RenderAiSelection tag={answer.tag} />
                    </div>
                )
            }),
        [question.answers, selectedOptions, handleMultiOptionSelect, RenderAiSelection]
    );

    return (
        <>
            {
                currentQuestion > 0 && (
                    <button className="tangent_backbtn_custom tangent_backbtn_mob_custom tngnt-absolute tngnt-top-0 tngnt-self-start tngnt-m-4 tngnt-text-[30px]" onClick={handleBackBtn}>
                        {backBtnConfig ? <img src={backBtnConfig.backBtn_icon} alt="back button" /> : <IoIosArrowRoundBack color="#929292" size={"3rem"} />}
                    </button>
                )
            }
            <div className={`tangent_question_screen_custom tangent_question_screen_mob_custom tangent_${question._id}_question_screen_custom tangent_${question._id}_question_screen_mob_custom tngnt-flex tngnt-flex-col tngnt-items-center tngnt-gap-4 sm:tngnt-gap-6 tngnt-p-3 sm:tngnt-p-6 tngnt-pt-16 sm:tngnt-pt-16 tngnt-justify-center tngnt-w-[100%]`} id={`comp_${question._id}`}>
                <div className={`tangent_global_style tangent_global_style_custom tangent_question_text tangent_question_text_custom tangent_question_text_mob_custom tangent_${question._id}_question_text_custom tangent_${question._id}_question_text_mob_custom tngnt-text-center tngnt-text-[20px] tngnt-ml-[15px] tngnt-mr-[15px] tngnt-w-[90%]`} dangerouslySetInnerHTML={{ __html: eval('`' + question.questionText + '`') }} />
                {question.imgUrl ? (
                    <img className={`tangent_question_image_custom tangent_${question._id}_question_image_custom tngnt-max-w-[inherit] tngnt-w-[100%] tngnt-max-h-[300px] tngnt-object-contain`} src={question.imgUrl} alt="question image" />
                ) : null}
                <div className={`tangent_options_container_style tangent_options_container_custom tangent_options_container_mob_custom tangent_${question._id}_options_container_custom tangent_${question._id}_options_container_mob_custom tngnt-items-stretch tngnt-flex tngnt-flex-wrap tngnt-justify-center tngnt-gap-7 tngnt-my-6 tngnt-w-[90%] sm:tngnt-w-[100%]`}>
                    {renderedAnswers}
                </div>
                {question.cta.map((cta) => (
                    <button
                        key={cta._id}
                        className={(cta.ctaType === "skip" ? `tangent_survey_skip_button_custom tangent_survey_skip_button_custom tangent_survey_skip_button_mob_custom tangent_${question._id}_survey_skip_button_custom tangent_${question._id}_survey_skip_button_mob_custom ` : `tangent_survey_next_button_custom tangent_survey_next_button_mob_custom tangent_${question._id}_survey_next_button_custom tangent_${question._id}_survey_next_button_mob_custom `) + "tangent_global_style tangent_global_style_custom tngnt-p-2 tngnt-w-[90%] sm:tngnt-w-[50%] tngnt-text-[16px] " + btnStyle[cta.ctaType]}
                        onClick={cta.ctaType === "next" ? () => handleSubmitBtn(cta) : () => updateUserResponse(constructResponse(cta))}
                        dangerouslySetInnerHTML={{ __html: cta.ctaText }}
                    />
                ))}
            </div >
            {showWarning && <FeedbackModal message={t('warning')} toggleShow={() => setShowWarning(false)} />}
            {optionLimitWarning && <FeedbackModal message={`${t('choiceLimitWarn1')} ${question.selectedOptionCount} ${t('choiceLimitWarn2')}.`} toggleShow={() => setOptionLimitWarning(false)} />}
            {
                question?.helpText && <div className="tangent_global_style tangent_global_style_custom tangent_helpText_custom tngnt-text-center tngnt-p-2 tngnt-mt-6 tngnt-text-[14px] tngnt-leading-[20px]" dangerouslySetInnerHTML={{ __html: eval('`' + question.helpText + '`') }}></div>
            }
        </>
    );
};

export default MultiChoice;